import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";

/**
 * NotchPanel — the general-purpose FLAT surface in the hex-notch silhouette: the
 * base container of the notch design language. Distinct from its neighbours:
 * `ArtifactTile` is artifact-specific, `GlassSurface` is a frosted FLOATING
 * panel, the `Card` family is rounded-rect — NotchPanel is the plain notched box
 * you put arbitrary content in (sections, cards, toolbars).
 *
 * Two clip-safe details the notch forces (a `clip-path` slices outer
 * `box-shadow`s + real `border`s): elevation is a `.elev-*` `drop-shadow`
 * (survives the clip, traces the notch), and the `keyline` is an `nx-rim` RING
 * overlay rather than a border. `asChild` renders the consumer's element (e.g.
 * a `<section>`) via Radix Slot.
 */
const panelVariants = cva("relative bg-card text-card-foreground", {
  variants: {
    elev: {
      none: "",
      soft: "elev-soft",
      card: "elev-card",
      panel: "elev-panel",
    },
    pad: {
      none: "",
      sm: "p-3",
      default: "p-5",
      lg: "p-7",
    },
  },
  defaultVariants: { elev: "card", pad: "default" },
});

export interface NotchPanelProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof panelVariants> {
  /** Notch chamfer size. Default `m` (panels are larger surfaces). */
  notch?: NxSize;
  /** Draw the hairline `nx-rim` keyline that traces the notch. Default `true`. */
  keyline?: boolean;
  /** Render the consumer's element (e.g. a `<section>`) via Radix Slot. */
  asChild?: boolean;
}

export const NotchPanel = React.forwardRef<HTMLDivElement, NotchPanelProps>(
  (
    { className, notch = "m", elev, pad, keyline = true, asChild = false, children, ...rest },
    ref,
  ) => {
    const Comp = asChild ? Slot : "div";
    return (
      <Comp
        ref={ref as React.Ref<HTMLDivElement>}
        className={cn(nx[notch], panelVariants({ elev, pad }), className)}
        {...rest}
      >
        {keyline ? (
          <span
            aria-hidden
            className="nx-rim pointer-events-none absolute inset-0 [--nx-rim:hsl(var(--border))]"
            style={{ clipPath: NX_RING_CLIP[notch] }}
          />
        ) : null}
        <Slottable>{children}</Slottable>
      </Comp>
    );
  },
);

NotchPanel.displayName = "NotchPanel";
